import { useState } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useLanguage } from "@/hooks/useLanguage";

interface Testimonial {
  name: string;
  role: string;
  company: string;
  quote: string;
  website: string | null;
}

const PartnerTestimonials = () => {
  const { t, tObject } = useLanguage();
  const testimonials = tObject<Testimonial[]>("partnerPage.testimonials");
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextTestimonial = () => setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  const prevTestimonial = () => setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">{t("partnerPage.testimonialsTitle")}</h2>
          <p className="text-lg text-muted-foreground">{t("partnerPage.testimonialsDescription")}</p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Card className="border-border/50 shadow-lg">
            <CardContent className="p-8 md:p-12">
              <Quote className="w-12 h-12 text-accent/30 mb-6" />
              <blockquote className="text-xl md:text-2xl text-foreground leading-relaxed mb-8">
                "{testimonials[currentIndex].quote}"
              </blockquote>
              <div className="flex items-center gap-4">
                <Avatar className="w-14 h-14">
                  <AvatarFallback className="bg-accent/10 text-accent text-lg font-semibold">
                    {testimonials[currentIndex].name.split(" ").map((n) => n[0]).join("")}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-semibold text-foreground">{testimonials[currentIndex].name}</div>
                  <div className="text-muted-foreground">{testimonials[currentIndex].role} / {testimonials[currentIndex].company}</div>
                  {testimonials[currentIndex].website && (
                    <a href={`https://${testimonials[currentIndex].website}`} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline text-sm">
                      {testimonials[currentIndex].website}
                    </a>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center justify-center gap-4 mt-8">
            <Button variant="outline" size="icon" onClick={prevTestimonial} className="rounded-full"><ChevronLeft className="w-5 h-5" /></Button>
            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button key={index} onClick={() => setCurrentIndex(index)} className={`w-2.5 h-2.5 rounded-full transition-colors ${index === currentIndex ? "bg-accent" : "bg-border"}`} />
              ))}
            </div>
            <Button variant="outline" size="icon" onClick={nextTestimonial} className="rounded-full"><ChevronRight className="w-5 h-5" /></Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnerTestimonials;
